import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';

export interface SavingsGoal {
  targetDate: Date;
  homePrice: number;
  downPaymentPercentage: number;
  downPaymentAmount: number;
  currentSavings: number;
  closingCostsEstimate: number;
  emergencyFundRecommended: number;
  totalNeeded: number;
  monthsUntilTarget: number;
  monthlyContributionNeeded: number;
  weeklyContributionNeeded: number;
  biweeklyContributionNeeded: number;
  interestEarned: number;
  scenarios: SavingsScenario[];
}

export interface SavingsScenario {
  name: string;
  downPaymentPercent: number;
  targetMonths: number;
  monthlyContribution: number;
  totalAmount: number;
  pros: string[];
  cons: string[];
  recommended: boolean;
}

export const calculateSavingsGoal = (
  formData: EnhancedUnifiedFormData,
  targetDate: Date,
  currentSavings: number,
  estimatedMonthlyPayment: number
): SavingsGoal => {
  const homePrice = formData.propertyLoan.homePrice;
  const downPaymentPercent = formData.propertyLoan.downPaymentPercentage;
  const downPaymentAmount = homePrice * (downPaymentPercent / 100);
  
  const closingCostsEstimate = homePrice * 0.03;
  const emergencyFundRecommended = estimatedMonthlyPayment * 3;
  const totalNeeded = downPaymentAmount + closingCostsEstimate + emergencyFundRecommended;
  const amountToSave = Math.max(0, totalNeeded - currentSavings);
  
  const today = new Date();
  const monthsUntilTarget = Math.max(1, 
    (targetDate.getFullYear() - today.getFullYear()) * 12 + 
    (targetDate.getMonth() - today.getMonth())
  );
  
  const monthlyContributionNeeded = amountToSave / monthsUntilTarget;
  const weeklyContributionNeeded = monthlyContributionNeeded / 4.33;
  const biweeklyContributionNeeded = monthlyContributionNeeded / 2.15;
  
  // Calculate interest with high-yield savings (4% APY)
  const interestRate = 0.04 / 12;
  let balance = currentSavings;
  let totalInterest = 0;
  
  for (let month = 0; month < monthsUntilTarget; month++) {
    balance += monthlyContributionNeeded;
    const monthlyInterest = balance * interestRate;
    totalInterest += monthlyInterest;
    balance += monthlyInterest;
  }
  
  const scenarios = generateSavingsScenarios(homePrice, monthsUntilTarget, currentSavings);
  
  return {
    targetDate,
    homePrice,
    downPaymentPercentage: downPaymentPercent,
    downPaymentAmount,
    currentSavings,
    closingCostsEstimate,
    emergencyFundRecommended,
    totalNeeded,
    monthsUntilTarget,
    monthlyContributionNeeded,
    weeklyContributionNeeded,
    biweeklyContributionNeeded,
    interestEarned: totalInterest,
    scenarios
  };
};

const generateSavingsScenarios = (
  homePrice: number,
  baseMonths: number,
  currentSavings: number
): SavingsScenario[] => {
  const scenarios: SavingsScenario[] = [];
  const downPaymentOptions = [3.5, 5, 10, 15, 20];
  const timeframeOptions = [
    Math.max(6, baseMonths - 6),
    baseMonths,
    baseMonths + 6,
    baseMonths + 12
  ];
  
  downPaymentOptions.forEach(dpPercent => {
    const closingCosts = homePrice * 0.03;
    const downPayment = homePrice * (dpPercent / 100);
    const totalNeeded = downPayment + closingCosts;
    const amountToSave = Math.max(0, totalNeeded - currentSavings);
    
    timeframeOptions.forEach(months => {
      if (months <= 0) return;
      
      const monthlyContribution = amountToSave / months;
      const years = months / 12;
      
      scenarios.push({
        name: `${dpPercent}% down in ${years.toFixed(1)} years`,
        downPaymentPercent: dpPercent,
        targetMonths: months,
        monthlyContribution,
        totalAmount: totalNeeded,
        pros: [
          dpPercent >= 20 ? 'No PMI required' : '',
          dpPercent >= 10 ? 'Lower interest rates' : 'Lower monthly savings needed',
          months <= baseMonths ? 'Buy sooner' : 'More time to save'
        ].filter(Boolean),
        cons: [
          dpPercent < 20 ? 'PMI required' : '',
          dpPercent < 10 ? 'Higher rates possible' : '',
          monthlyContribution > 1000 ? 'High monthly commitment' : '',
          months > baseMonths + 6 ? 'Delayed purchase' : ''
        ].filter(Boolean),
        recommended: dpPercent === 20 && months === baseMonths
      });
    });
  });
  
  return scenarios.sort((a, b) => {
    if (a.recommended && !b.recommended) return -1;
    if (!a.recommended && b.recommended) return 1;
    return a.downPaymentPercent - b.downPaymentPercent;
  });
};
